import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Confetti from "react-confetti";
import { api, naira, Icon, computeCashbackRate } from "../lib/core";

// ============================================================
// Data hook
// ============================================================
export const useGamification = () => {
  const [summary, setSummary] = useState(null);
  const refresh = useCallback(() => {
    api.get("/gamification/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { summary, refresh };
};

// Animated count-up of a naira amount (used for wheel-win reward pop)
const CountUpNaira = ({ amount = 0, duration = 900 }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setVal(Math.round(amount * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [amount, duration]);
  return <>{naira(val)}</>;
};


const useWindowSize = () => {
  const [size, setSize] = useState({ width: typeof window !== "undefined" ? window.innerWidth : 1200, height: typeof window !== "undefined" ? window.innerHeight : 800 });
  useEffect(() => {
    const on = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return size;
};

const TIER_ICON = { Bronze: "military_tech", Silver: "workspace_premium", Gold: "emoji_events", Platinum: "diamond" };

// ============================================================
// Summary strip: Savings / Referral / Cashback
// ============================================================
export const HomeSummaryStrip = ({ summary }) => {
  const s = summary || {};
  const tiles = [
    { label: "Saved this month", value: naira(s.savings_month || 0), icon: "savings", glow: "#10B981", testid: "gami-savings-month" },
    { label: "Referral earnings", value: naira(s.referral_earnings || 0), icon: "diversity_3", glow: "#06B6D4", testid: "gami-referral-earnings" },
    { label: "Total saved", value: naira(s.achievements?.total_saved || 0), icon: "redeem", glow: "#F59E0B", testid: "gami-total-saved" },
  ];
  return (
    <div className="grid grid-cols-3 gap-3" data-testid="gami-summary-strip">
      {tiles.map((t, i) => (
        <motion.div key={t.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
          className="relative overflow-hidden rounded-2xl bg-[#111928] border border-white/10 p-3.5" data-testid={t.testid}>
          <div className="absolute -right-3 -top-3 w-16 h-16 rounded-full blur-2xl opacity-40" style={{ background: t.glow }} />
          <div className="relative flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: t.glow }}>
            <Icon name={t.icon} className="text-sm" /> <span className="text-slate-400 truncate">{t.label}</span>
          </div>
          <p className="relative mt-1.5 text-white font-extrabold text-base sm:text-lg tnum font-display leading-tight">{t.value}</p>
        </motion.div>
      ))}
    </div>
  );
};

// ============================================================
// Quick actions
// ============================================================
export const QuickActionsRow = ({ onNavigate, onOpenWallet, onOpenGroups, rates }) => {
  const airtimeRate = computeCashbackRate(rates, "airtime", null);
  const dataRate = computeCashbackRate(rates, "data", null);
  const actions = [
    { icon: "smartphone", label: "Airtime", grad: "from-red-500 to-rose-600", onClick: () => onNavigate("airtime"), badge: airtimeRate > 0 ? `${airtimeRate}%` : null, testid: "qa-airtime" },
    { icon: "wifi", label: "Data", grad: "from-emerald-500 to-teal-600", onClick: () => onNavigate("data"), badge: dataRate > 0 ? `${dataRate}%` : null, testid: "qa-data" },
    { icon: "add_card", label: "Fund", grad: "from-violet-500 to-indigo-600", onClick: onOpenWallet, testid: "qa-fund" },
    { icon: "groups", label: "Groups", grad: "from-amber-500 to-orange-600", onClick: onOpenGroups, testid: "qa-groups" },
  ];
  return (
    <div className="grid grid-cols-4 gap-3" data-testid="gami-quick-actions">
      {actions.map((a) => (
        <motion.button key={a.label} whileTap={{ scale: 0.94 }} onClick={a.onClick} data-testid={a.testid}
          className="relative flex flex-col items-center gap-2 group">
          <span className={`relative w-14 h-14 rounded-2xl bg-gradient-to-br ${a.grad} flex items-center justify-center shadow-lg`}>
            <Icon name={a.icon} className="text-white text-2xl" />
            {a.badge && <span className="absolute -top-1.5 -right-1.5 bg-white text-[9px] font-black text-slate-900 px-1.5 py-0.5 rounded-full shadow">{a.badge}</span>}
          </span>
          <span className="text-xs font-semibold text-slate-300">{a.label}</span>
        </motion.button>
      ))}
    </div>
  );
};

// ============================================================
// Because you usually...
// ============================================================
export const BecauseYouUsually = ({ usual, onBuy }) => {
  if (!usual) return null;
  const label = usual.service_type === "data"
    ? `Your usual ${usual.provider} data — ${naira(usual.amount)}`
    : `Your usual ${usual.provider} airtime — ${naira(usual.amount)}`;
  return (
    <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} data-testid="gami-because-usually"
      className="relative overflow-hidden rounded-3xl p-[1.5px] bg-gradient-to-r from-red-500 via-fuchsia-500 to-cyan-400"
      style={{ backgroundSize: "200% auto", animation: "gami-shine 4s linear infinite" }}>
      <div className="rounded-3xl bg-[#111928] p-5 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wider text-cyan-400">Because you usually…</p>
          <p className="text-white font-extrabold text-lg font-display mt-1 truncate">{label}</p>
          <p className="text-slate-400 text-xs mt-0.5">Bought {usual.count} times · one tap to repeat</p>
        </div>
        <motion.button whileTap={{ scale: 0.95 }} onClick={() => onBuy(usual)} data-testid="gami-buy-again"
          className="shrink-0 bg-white text-slate-900 font-bold text-sm px-5 py-3 rounded-2xl hover:bg-slate-100 transition-colors">
          Buy Again
        </motion.button>
      </div>
    </motion.div>
  );
};

// ============================================================
// Today's offers carousel
// ============================================================
export const OffersCarousel = () => {
  const [offers, setOffers] = useState([]);
  useEffect(() => { api.get("/offers").then((r) => setOffers(r.data.items || [])).catch(() => {}); }, []);
  if (!offers.length) return null;
  return (
    <div data-testid="gami-offers">
      <SectionHeader title="Today's Offers" subtitle="Curated brand deals" icon="local_offer" />
      <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1 snap-x">
        {offers.map((o, i) => (
          <motion.div key={o.id} whileTap={{ scale: 0.97 }} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
            data-testid={`gami-offer-${o.id}`}
            className="relative shrink-0 w-60 h-36 rounded-3xl overflow-hidden snap-start cursor-pointer group">
            <img src={o.image} alt={o.brand} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(11,18,32,0.95) 10%, rgba(11,18,32,0.25) 60%, transparent)" }} />
            <span className="absolute top-3 left-3 text-[10px] font-bold px-2 py-1 rounded-full text-white" style={{ background: o.accent }}>{o.category}</span>
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <p className="text-white/70 text-xs font-semibold">{o.emoji} {o.brand}</p>
              <p className="text-white font-extrabold font-display text-base leading-tight">{o.title}</p>
              <p className="text-white/60 text-[11px]">{o.subtitle}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// ============================================================
// Trending deals
// ============================================================
export const TrendingDeals = ({ onBuy }) => {
  const [deals, setDeals] = useState([]);
  useEffect(() => { api.get("/deals").then((r) => setDeals(r.data.items || [])).catch(() => {}); }, []);
  if (!deals.length) return null;
  const tagColor = { "Best Value": "#10B981", "Most Popular": "#E11D2E", "Weekend Pick": "#F97316" };
  return (
    <div data-testid="gami-deals">
      <SectionHeader title="Trending Deals" subtitle="Best value bundles right now" icon="trending_up" />
      <div className="grid sm:grid-cols-3 gap-3">
        {deals.map((d, i) => (
          <motion.button key={`${d.provider}-${i}`} whileTap={{ scale: 0.97 }} onClick={() => onBuy && onBuy(d)} data-testid={`gami-deal-${d.provider}`}
            className="text-left rounded-2xl bg-[#111928] border border-white/10 p-4 hover:border-white/25 transition-colors">
            <span className="text-[10px] font-black px-2 py-0.5 rounded-full text-white" style={{ background: tagColor[d.tag] || "#8B5CF6" }}>{d.tag}</span>
            <p className="text-white font-bold mt-2">{d.provider} Data</p>
            <p className="text-slate-400 text-xs mt-0.5 line-clamp-1">{d.description}</p>
            <p className="text-white font-extrabold font-display text-lg mt-1 tnum">{naira(d.amount)}</p>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

// ============================================================
// Achievements + streak + tier + spin
// ============================================================
export const AchievementsRow = ({ summary, onSpin }) => {
  const s = summary || {};
  const streak = s.streak?.current || 0;
  const saved = s.achievements?.total_saved || 0;
  const earned = s.achievements?.total_earned || 0;
  const tier = s.referral_tier || {};
  const spinAvail = s.spin?.available;
  return (
    <div data-testid="gami-achievements">
      <SectionHeader title="Your Achievements" subtitle="Keep the streak alive" icon="workspace_premium" />
      <div className="grid grid-cols-3 gap-3">
        <Achievement icon="local_fire_department" color="#F97316" value={`${streak}`} label={`Day streak${s.streak?.active_today ? "" : " · top up today!"}`} testid="gami-streak" big />
        <Achievement icon="savings" color="#10B981" value={naira(saved)} label="Total saved" testid="gami-saved" />
        <Achievement icon="payments" color="#06B6D4" value={naira(earned)} label="Total earned" testid="gami-earned" />
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        {/* Referral tier */}
        <div className="rounded-2xl bg-[#111928] border border-white/10 p-4" data-testid="gami-tier">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${tier.color}22`, color: tier.color }}>
                <Icon name={TIER_ICON[tier.tier] || "military_tech"} />
              </span>
              <div>
                <p className="text-white font-bold font-display">{tier.tier || "Bronze"} Referrer</p>
                <p className="text-slate-400 text-xs">{tier.count || 0} friends invited</p>
              </div>
            </div>
          </div>
          {tier.next_tier && (
            <div className="mt-3">
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${tier.progress || 0}%`, background: tier.color }} />
              </div>
              <p className="text-slate-400 text-[11px] mt-1.5">{tier.to_next} more to reach <span className="font-semibold" style={{ color: tier.color }}>{tier.next_tier}</span></p>
            </div>
          )}
        </div>

        {/* Spin card */}
        <button onClick={onSpin} data-testid="gami-spin-card"
          className={`relative overflow-hidden rounded-2xl p-4 text-left border transition-all ${spinAvail ? "border-transparent bg-gradient-to-br from-violet-600 to-fuchsia-600 shadow-lg shadow-fuchsia-600/30" : "bg-[#111928] border-white/10"}`}>
          <div className="flex items-center gap-3">
            <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${spinAvail ? "bg-white/20" : "bg-white/5"}`}>
              <Icon name="casino" className={`text-2xl ${spinAvail ? "text-white" : "text-slate-400"}`} />
            </span>
            <div>
              <p className={`font-bold font-display ${spinAvail ? "text-white" : "text-slate-200"}`}>Rewards Wheel</p>
              <p className={`text-xs ${spinAvail ? "text-white/80" : "text-slate-400"}`}>
                {spinAvail ? `${s.spin.spins_available} spin${s.spin.spins_available > 1 ? "s" : ""} ready — tap to win!` : `${s.spin?.txns_to_next ?? 5} more top-ups to earn a spin`}
              </p>
            </div>
          </div>
          {spinAvail && <span className="absolute top-2 right-2 text-[10px] font-black bg-white text-fuchsia-700 px-2 py-0.5 rounded-full animate-pulse">SPIN</span>}
        </button>
      </div>
    </div>
  );
};

const Achievement = ({ icon, color, value, label, testid, big }) => (
  <div className="rounded-2xl bg-[#111928] border border-white/10 p-4 flex flex-col items-center text-center" data-testid={testid}>
    <span className="w-11 h-11 rounded-xl flex items-center justify-center mb-2" style={{ background: `${color}22`, color }}>
      <Icon name={icon} className="text-2xl" />
    </span>
    <p className={`text-white font-black font-display tnum ${big ? "text-2xl" : "text-base"}`}>{value}</p>
    <p className="text-slate-400 text-[11px] mt-0.5 leading-tight">{label}</p>
  </div>
);

export const SectionHeader = ({ title, subtitle, icon }) => (
  <div className="flex items-center gap-2 mb-3">
    <span className="w-8 h-8 rounded-xl bg-red-50 flex items-center justify-center"><Icon name={icon} className="text-red-600" /></span>
    <div>
      <h3 className="text-slate-900 font-extrabold font-display leading-none">{title}</h3>
      {subtitle && <p className="text-slate-500 text-xs mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

// ============================================================
// Rewards wheel modal
// ============================================================
export const RewardsWheelModal = ({ open, onClose, wheel, onSpun }) => {
  const { width, height } = useWindowSize();
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const segs = wheel && wheel.length ? wheel : [];
  const n = segs.length || 1;
  const seg = 360 / n;

  const spin = async () => {
    if (spinning) return;
    setSpinning(true); setResult(null);
    try {
      const r = await api.post("/gamification/spin");
      const prize = r.data.prize;
      const idx = Math.max(0, segs.findIndex((p) => p.id === prize.id));
      // land the winning segment center under the top pointer
      const target = 360 * 6 - (idx * seg + seg / 2);
      setRotation((prev) => prev - (prev % 360) + target);
      setTimeout(() => { setResult(r.data); setSpinning(false); onSpun && onSpun(); }, 4200);
    } catch (e) {
      setSpinning(false);
      setResult({ error: e.response?.data?.detail || "No spins available right now." });
    }
  };

  const gradient = `conic-gradient(${segs.map((p, i) => `${p.color} ${i * seg}deg ${(i + 1) * seg}deg`).join(", ")})`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="rewards-wheel-modal">
          {result?.won && <Confetti width={width} height={height} recycle={false} numberOfPieces={320} />}
          <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} className="relative bg-[#0B1220] border border-white/10 rounded-3xl p-6 w-full max-w-sm text-center">
            <button onClick={onClose} data-testid="wheel-close" className="absolute top-4 right-4 text-slate-400 hover:text-white"><Icon name="close" /></button>
            <h3 className="text-white font-extrabold text-xl font-display">Rewards Wheel</h3>
            <p className="text-slate-400 text-sm mb-4">Spin to win instant cashback</p>

            <div className="relative mx-auto w-64 h-64">
              {/* pointer */}
              <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-10" style={{ width: 0, height: 0, borderLeft: "12px solid transparent", borderRight: "12px solid transparent", borderTop: "20px solid #fff" }} />
              <motion.div className="w-64 h-64 rounded-full border-4 border-white/20"
                style={{ background: gradient }}
                animate={{ rotate: rotation }} transition={{ duration: 4, ease: [0.15, 0.8, 0.2, 1] }}>
                {segs.map((p, i) => {
                  const angle = i * seg + seg / 2;
                  const label = p.label.replace(" Cashback", "");
                  // Flip based on the label's CURRENT on-screen angle (incl. wheel rotation)
                  // so no label is ever upside-down at rest — even the winning one after a spin.
                  const eff = (((angle + rotation) % 360) + 360) % 360;
                  const flip = eff > 90 && eff < 270;
                  return (
                    <div key={p.id + i} className="absolute inset-0 pointer-events-none" style={{ transform: `rotate(${angle}deg)` }}>
                      <div className="absolute left-1/2 -translate-x-1/2" style={{ top: "12px", width: "64px" }}>
                        <span className="block text-white text-[10px] font-bold text-center leading-[1.05]"
                          style={{ textShadow: "0 1px 2px rgba(0,0,0,0.75)", transform: flip ? "rotate(180deg)" : undefined }}>
                          {label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </motion.div>
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[#0B1220] border-4 border-white/30 flex items-center justify-center">
                <Icon name="bolt" className="text-red-500" />
              </div>
            </div>

            {!result && (
              <motion.button whileTap={{ scale: 0.95 }} onClick={spin} disabled={spinning} data-testid="wheel-spin-btn"
                className="mt-6 w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold py-3.5 rounded-2xl disabled:opacity-60">
                {spinning ? "Spinning…" : "SPIN NOW"}
              </motion.button>
            )}
            {result && !result.error && (
              <div className="mt-6" data-testid="wheel-result">
                <p className="text-white font-extrabold text-lg font-display">{result.won ? "🎉 You won!" : "Almost!"}</p>
                {result.won && result.prize?.amount > 0 && (
                  <motion.div
                    initial={{ scale: 0.4, opacity: 0, y: 8 }}
                    animate={{ scale: [0.4, 1.15, 1], opacity: 1, y: 0 }}
                    transition={{ duration: 0.55, times: [0, 0.7, 1], ease: "easeOut" }}
                    className="mx-auto mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 px-4 py-1.5"
                    data-testid="wheel-reward-pop">
                    <Icon name="account_balance_wallet" className="text-emerald-400 text-base" />
                    <span className="text-emerald-300 font-extrabold text-xl tnum">+<CountUpNaira amount={result.prize.amount} /></span>
                  </motion.div>
                )}
                <p className="text-slate-300 text-sm mt-2">{result.won ? `${result.prize.label} added to your wallet` : "Better luck on your next spin"}</p>
                <button onClick={onClose} className="mt-4 w-full bg-white text-slate-900 font-bold py-3 rounded-2xl">Awesome</button>
              </div>
            )}
            {result?.error && (
              <div className="mt-6" data-testid="wheel-error">
                <p className="text-slate-300 text-sm">{result.error}</p>
                <button onClick={onClose} className="mt-4 w-full bg-white/10 text-white font-bold py-3 rounded-2xl">Close</button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ============================================================
// Post-purchase celebration
// ============================================================
export const PostPurchaseCelebration = ({ open, onClose, onSpin }) => {
  const { width, height } = useWindowSize();
  const [data, setData] = useState(null);
  const prevSummary = useRef(null);

  useEffect(() => {
    if (!open) return;
    setData(null);
    Promise.all([
      api.get("/gamification/summary").then((r) => r.data).catch(() => null),
      api.get("/transactions/me").then((r) => (r.data || [])[0]).catch(() => null),
    ]).then(([summary, tx]) => {
      setData({ summary, tx });
      prevSummary.current = summary;
    });
  }, [open]);

  const summary = data?.summary;
  const tx = data?.tx;
  const spinAvail = summary?.spin?.available;

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="purchase-celebration">
          <Confetti width={width} height={height} recycle={false} numberOfPieces={280} />
          <motion.div initial={{ scale: 0.85, y: 30 }} animate={{ scale: 1, y: 0 }} transition={{ type: "spring", damping: 14 }}
            className="relative bg-[#0B1220] border border-white/10 rounded-3xl p-6 w-full max-w-sm text-center overflow-hidden">
            <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-40 h-40 bg-emerald-500/20 rounded-full blur-3xl" />
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.15, type: "spring" }}
              className="relative mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
              <Icon name="check" className="text-white text-3xl" />
            </motion.div>
            <h3 className="relative text-white font-extrabold text-xl font-display mt-4">Top-up successful! 🎉</h3>
            {tx && <p className="relative text-slate-400 text-sm">{naira(tx.amount)} {tx.service_type} to {tx.customer_phone}</p>}

            <div className="relative grid grid-cols-3 gap-2 mt-5">
              <MiniStat icon="local_fire_department" color="#F97316" label="Streak" value={`${summary?.streak?.current ?? "—"} 🔥`} />
              <MiniStat icon="savings" color="#10B981" label="Saved" value={naira(summary?.savings_month || 0)} />
              <MiniStat icon="diversity_3" color="#06B6D4" label={summary?.referral_tier?.tier || "Tier"} value={`${summary?.referral_tier?.count ?? 0}`} />
            </div>

            {spinAvail ? (
              <button onClick={() => { onClose(); onSpin && onSpin(); }} data-testid="celebration-spin"
                className="mt-5 w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2">
                <Icon name="casino" /> Spin the wheel — you earned it!
              </button>
            ) : (
              <p className="mt-5 text-slate-400 text-xs">
                {summary?.spin?.txns_to_next ? `${summary.spin.txns_to_next} more top-ups to unlock a free spin 🎰` : "Keep it up!"}
              </p>
            )}
            <button onClick={onClose} data-testid="celebration-close" className="mt-2 w-full text-slate-400 text-sm py-2 hover:text-white">Done</button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const MiniStat = ({ icon, color, label, value }) => (
  <div className="rounded-xl bg-white/5 border border-white/10 p-2.5">
    <Icon name={icon} className="text-lg" style={{ color }} />
    <p className="text-white font-bold text-sm tnum mt-0.5">{value}</p>
    <p className="text-slate-400 text-[10px]">{label}</p>
  </div>
);
